import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Organization } from '../services/auth';
import { organizationService } from '../services/organizations';
import { useAuth } from './AuthContext';

interface OrganizationMember {
  id: string;
  user_id: string;
  email: string;
  role: 'admin' | 'manager' | 'operator' | 'viewer';
  joined_at: string;
}

interface OrganizationInvitation {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string;
  invitation_url?: string;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  members: OrganizationMember[];
  invitations: OrganizationInvitation[];
  isLoading: boolean;
  error: string | null;
  
  // Organization management
  switchOrganization: (orgId: string) => Promise<void>;
  createOrganization: (name: string) => Promise<void>;
  updateOrganization: (name: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  
  // Member management
  inviteMember: (email: string, role: string) => Promise<string | undefined>;
  removeMember: (memberId: string) => Promise<void>;
  updateMemberRole: (memberId: string, role: string) => Promise<void>;
  refreshMembers: () => Promise<void>;
  
  // Invitation management
  cancelInvitation: (invitationId: string) => Promise<void>;
  refreshInvitations: () => Promise<void>;
  
  // Utils
  hasPermission: (resource: string, action: string) => boolean;
  isOrgAdmin: () => boolean;
  clearError: () => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

interface OrganizationProviderProps {
  children: ReactNode;
}

// Permission matrix for roles
const ROLE_PERMISSIONS = {
  admin: {
    all: true,
    stores: ['view', 'create', 'edit', 'delete'],
    products: ['view', 'create', 'edit', 'delete', 'sync'],
    jobs: ['view', 'create', 'approve', 'reject', 'cancel'],
    analytics: ['view', 'export'],
    team: ['view', 'invite', 'remove', 'edit'],
    billing: ['view', 'manage']
  },
  manager: {
    stores: ['view', 'create', 'edit', 'delete'],
    products: ['view', 'create', 'edit', 'delete', 'sync'],
    jobs: ['view', 'create', 'approve', 'reject', 'cancel'],
    analytics: ['view', 'export'],
    team: ['view', 'invite'],
    billing: []
  },
  operator: {
    stores: ['view'],
    products: ['view', 'sync'],
    jobs: ['view', 'create'],
    analytics: ['view'],
    team: ['view'],
    billing: []
  },
  viewer: {
    stores: ['view'],
    products: ['view'],
    jobs: ['view'],
    analytics: ['view'],
    team: ['view'],
    billing: []
  }
};

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  // Check if user has permission for a resource and action
  const hasPermission = (resource: string, action: string): boolean => {
    if (!user?.organization_role) return false;
    
    const permissions = ROLE_PERMISSIONS[user.organization_role as keyof typeof ROLE_PERMISSIONS];
    if (!permissions) return false;
    
    // Check for admin all access
    if ('all' in permissions && permissions.all === true) return true;
    
    // Check specific permission
    const resourcePermissions = permissions[resource as keyof typeof permissions] as string[] | undefined;
    if (Array.isArray(resourcePermissions)) {
      return resourcePermissions.includes(action);
    }
    
    return false;
  };

  const isOrgAdmin = (): boolean => {
    return user?.organization_role === 'admin';
  };

  // Load user's organizations
  const refreshOrganizations = async () => {
    if (!isAuthenticated) {
      setOrganizations([]);
      setCurrentOrganization(null);
      return;
    }

    setIsLoading(true);
    try {
      const orgs = await organizationService.getOrganizations();
      console.log('Fetched organizations:', orgs);
      console.log('User current_organization_id:', user?.current_organization_id);
      
      setOrganizations(orgs);
      
      // Set current organization from user data
      if (user?.current_organization_id) {
        const current = orgs.find(org => org.id === user.current_organization_id);
        console.log('Found current organization:', current);
        if (current) {
          setCurrentOrganization(current);
        }
      } else if (orgs && orgs.length > 0) {
        // Default to first organization if no current set
        console.log('Setting first organization as current:', orgs[0]);
        setCurrentOrganization(orgs[0]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      setError('Failed to load organizations');
    } finally {
      setIsLoading(false);
    }
  };

  // Switch to a different organization
  const switchOrganization = async (orgId: string) => {
    setIsLoading(true);
    try {
      await organizationService.switchOrganization(orgId);
      
      // Update current organization
      const org = organizations.find(o => o.id === orgId);
      if (org) {
        setCurrentOrganization(org);
      }
      
      // Refresh auth context to get new permissions
      window.location.reload(); // Simple reload to refresh all contexts
      
      setError(null);
    } catch (err) {
      console.error('Failed to switch organization:', err);
      setError('Failed to switch organization');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new organization
  const createOrganization = async (name: string) => {
    setIsLoading(true);
    try {
      const newOrg = await organizationService.createOrganization(name);
      setOrganizations([...organizations, newOrg]);
      
      // Switch to the new organization
      await switchOrganization(newOrg.id);
      
      setError(null);
    } catch (err) {
      console.error('Failed to create organization:', err);
      setError('Failed to create organization');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrganization = async (name: string) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    setIsLoading(true);
    try {
      const result = await organizationService.updateOrganization(currentOrganization.id, { name });
      
      // Update current organization with new data
      const updatedOrg = result.organization;
      setCurrentOrganization(updatedOrg);
      
      // Update in organizations list
      setOrganizations(organizations.map(org => 
        org.id === updatedOrg.id ? updatedOrg : org
      ));
      
      setError(null);
    } catch (err) {
      console.error('Failed to update organization:', err);
      setError('Failed to update organization');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Load organization members
  const refreshMembers = async () => {
    if (!currentOrganization) {
      setMembers([]);
      return;
    }

    setIsLoading(true);
    try {
      const memberList = await organizationService.getMembers(currentOrganization.id);
      setMembers(memberList);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setError('Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  };

  // Load organization invitations
  const refreshInvitations = async () => {
    if (!currentOrganization) {
      setInvitations([]);
      return;
    }

    setIsLoading(true);
    try {
      const inviteList = await organizationService.getInvitations(currentOrganization.id);
      setInvitations(inviteList);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
      setError('Failed to load invitations');
    } finally {
      setIsLoading(false);
    }
  };

  // Invite a new member and return the invitation URL
  const inviteMember = async (email: string, role: string) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    try {
      const result = await organizationService.inviteMember(currentOrganization.id, email, role);
      await refreshInvitations();
      setError(null);
      return result.invitation.invitation_url; // Return the invitation URL
    } catch (err) {
      console.error('Failed to invite member:', err);
      setError('Failed to send invitation');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Remove a member
  const removeMember = async (memberId: string) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    try {
      await organizationService.removeMember(currentOrganization.id, memberId);
      // Refresh the members list after successful removal
      await refreshMembers();
      setError(null);
    } catch (err) {
      console.error('Failed to remove member:', err);
      setError('Failed to remove team member');
      // Still try to refresh to get latest state
      try {
        await refreshMembers();
      } catch (refreshErr) {
        console.error('Failed to refresh members after error:', refreshErr);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Update member role
  const updateMemberRole = async (memberId: string, role: string) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    try {
      await organizationService.updateMemberRole(currentOrganization.id, memberId, role);
      // Refresh the members list after successful update
      await refreshMembers();
      setError(null);
    } catch (err) {
      console.error('Failed to update member role:', err);
      setError('Failed to update member role');
      // Still try to refresh to get latest state
      try {
        await refreshMembers();
      } catch (refreshErr) {
        console.error('Failed to refresh members after error:', refreshErr);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel an invitation
  const cancelInvitation = async (invitationId: string) => {
    setIsLoading(true);
    try {
      await organizationService.cancelInvitation(invitationId);
      // Refresh the invitations list after successful cancellation
      await refreshInvitations();
      setError(null);
    } catch (err) {
      console.error('Failed to cancel invitation:', err);
      setError('Failed to cancel invitation');
      // Still try to refresh to get latest state
      try {
        await refreshInvitations();
      } catch (refreshErr) {
        console.error('Failed to refresh invitations after error:', refreshErr);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Load organizations when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      refreshOrganizations();
    }
  }, [isAuthenticated, user?.id]);

  // Load members and invitations when organization changes
  useEffect(() => {
    if (currentOrganization) {
      refreshMembers();
      refreshInvitations();
    }
  }, [currentOrganization?.id]);

  const value: OrganizationContextType = {
    currentOrganization,
    organizations,
    members,
    invitations,
    isLoading,
    error,
    switchOrganization,
    createOrganization,
    updateOrganization,
    refreshOrganizations,
    inviteMember,
    removeMember,
    updateMemberRole,
    refreshMembers,
    cancelInvitation,
    refreshInvitations,
    hasPermission,
    isOrgAdmin,
    clearError,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};